import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DeleteOutlined, ScheduleOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Input, Modal, Spin, Tooltip, message } from "antd";
import calendarSrc from "./assets/appointments/calendar.svg";
import fileTextSrc from "./assets/appointments/file-text.svg";
import listSrc from "./assets/appointments/list.svg";
import plusSrc from "./assets/appointments/plus.svg";
import searchSrc from "./assets/appointments/search.svg";
import walletSrc from "./assets/appointments/wallet.svg";
import zapSrc from "./assets/appointments/zap.svg";
import ClientAppointmentAutoPlanDrawer from "./client-appointment-auto-plan-drawer";
import ClientAppointmentFormDrawer from "./client-appointment-form-drawer";
import ClientAppointmentPayDrawer from "./client-appointment-pay-drawer";
import ClientAppointmentPlanDaysDrawer from "./client-appointment-plan-days-drawer";
import ClientAppointmentTemplateModal from "./client-appointment-template-modal";
import {
  applyHealthUslTemplate,
  deleteHealthClientUsl,
  fetchHealthClientUsl
} from "./health-clients-api";
import { formatNumber, pick, toNumber } from "./health-clients-utils";
import "./client-appointments.css";

const VID_PAN_LABELS = {
  0: "Не планировать",
  1: "Каждый день",
  2: "Через день",
  7: "Раз в неделю",
  12: "Через день (сквозное)",
  100: "По времени"
};

const formatMoney = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return `${n.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ₽`;
};

const ToolbarIcon = ({ src }) => (
  <span className="client-appointments__btn-icon">
    <img src={src} alt="" />
  </span>
);

const formatVidPan = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return VID_PAN_LABELS[n] ?? String(n);
};

/** Можно открыть планирование: не «Не планировать» (по дням или по времени). */
const canPlanManual = (vidPan) => {
  const v = toNumber(vidPan);
  return v != null && v !== 0;
};

const isPlanByTime = (vidPan) => toNumber(vidPan) === 100;

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const hasDebt = (item) => {
  if (toNumber(item?.isPlat) !== 1) return false;
  const dolg = toNumber(item?.symaDolg);
  return dolg != null && Math.abs(dolg) > 0.005;
};

export const normalizeUslRow = (row) => {
  const idClient = toNumber(pick(row, "idClient", "IdClient"));
  const idStr = toNumber(pick(row, "idStr", "IdStr"));
  return {
    key: `${idClient}-${idStr}`,
    idClient,
    idStr,
    idUsl: toNumber(pick(row, "idUsl", "IdUsl")),
    tName: pick(row, "tName", "TName") ?? "",
    cnt: pick(row, "cnt", "Cnt"),
    cntPlan: pick(row, "cntPlan", "CntPlan"),
    vidPan: pick(row, "vidPan", "VidPan"),
    isPlat: pick(row, "isPlat", "IsPlat"),
    unlimit: pick(row, "unlimit", "Unlimit"),
    sotrName: pick(row, "sotrName", "SotrName") ?? "",
    komment: pick(row, "komment", "Komment") ?? "",
    zenaExp: pick(row, "zenaExp", "ZenaExp"),
    symaExp: pick(row, "symaExp", "SymaExp"),
    symaPlat: pick(row, "symaPlat", "SymaPlat"),
    symaDolg: pick(row, "symaDolg", "SymaDolg"),
    symaInc: pick(row, "symaInc", "SymaInc"),
    dateCreate: pick(row, "dateCreate", "DateCreate"),
    zenaSpr: pick(row, "zenaSpr", "ZenaSpr"),
    prcDiscount: pick(row, "prcDiscount", "PrcDiscount"),
    svName: pick(row, "svName", "SvName") ?? "",
    idSotr: toNumber(pick(row, "idSotr", "IdSotr")),
    idVrach: toNumber(pick(row, "idVrach", "IdVrach"))
  };
};

const MoneyBadge = ({ item }) => {
  const isPlat = toNumber(item?.isPlat) === 1;
  if (!isPlat) return null;

  const symaExp = toNumber(item?.symaExp) ?? 0;
  const symaPlat = toNumber(item?.symaPlat) ?? 0;
  const symaDolg = toNumber(item?.symaDolg) ?? 0;
  const debt = Math.abs(symaDolg) > 0.005;
  const tone = debt ? "debt" : "paid";

  const hint = (
    <div className="client-appointments__money-hint">
      <div>
        Стоимость: <b>{formatMoney(symaExp)}</b>
      </div>
      <div>
        Оплата: <b>{formatMoney(symaPlat)}</b>
      </div>
      <div>
        Долг: <b>{formatMoney(symaDolg)}</b>
      </div>
    </div>
  );

  return (
    <Tooltip title={hint}>
      <span
        className={`client-appointments__money client-appointments__money--${tone}`}
        onClick={(e) => e.stopPropagation()}
      >
        {formatMoney(symaExp)}
      </span>
    </Tooltip>
  );
};

const AppointmentCardView = ({ item, selected, onToggle, onEdit, onPlanDays }) => {
  const isPlat = toNumber(item.isPlat) === 1;
  const vidPan = formatVidPan(item.vidPan);
  const canPlan = canPlanManual(item.vidPan);
  const planByTime = isPlanByTime(item.vidPan);
  const cnt = toNumber(item.cnt) ?? 0;
  const cntPlan = toNumber(item.cntPlan) ?? 0;
  const planPct = cnt > 0 ? Math.min(100, Math.round((cntPlan / cnt) * 100)) : 0;
  const planTone = cntPlan <= 0 ? "empty" : cnt > 0 && cntPlan >= cnt ? "full" : "partial";

  return (
    <div
      className={`client-appointments__card${selected ? " client-appointments__card--selected" : ""}`}
      onClick={() => onEdit(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(item);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Изменить: ${item.tName || "назначение"}`}
    >
      <div className="client-appointments__card-main">
        <div className="client-appointments__check" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onChange={() => onToggle(item.key)} />
        </div>
        <div className="client-appointments__details">
          <div className="client-appointments__title-row">
            <h4 className="client-appointments__title" title={item.tName || "Без названия"}>
              {item.tName || "Без названия"}
            </h4>
            <div className="client-appointments__head-right" onClick={(e) => e.stopPropagation()}>
              <Tooltip
                title={
                  canPlan
                    ? planByTime
                      ? "Планирование по времени"
                      : "Планирование по дням"
                    : "Планирование недоступно"
                }
              >
                <Button
                  type="text"
                  size="small"
                  className="client-appointments__action"
                  icon={<ScheduleOutlined />}
                  aria-label="Планировать"
                  disabled={!canPlan}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlanDays(item);
                  }}
                />
              </Tooltip>
            </div>
          </div>
          <div className="client-appointments__meta">
            {isPlat ? (
              <span className="client-appointments__badge client-appointments__badge--plat">
                Платно
              </span>
            ) : null}
            {vidPan ? (
              <span className="client-appointments__badge client-appointments__badge--freq">
                {vidPan}
              </span>
            ) : null}
            {item.svName ? (
              <span className="client-appointments__assigned" title={`Врач: ${item.svName}`}>
                Врач: {item.svName}
              </span>
            ) : null}
            {item.sotrName ? (
              <span className="client-appointments__assigned" title={`Назначил: ${item.sotrName}`}>
                Назначил: {item.sotrName}
              </span>
            ) : null}
          </div>
          {item.komment ? <div className="client-appointments__comment">{item.komment}</div> : null}
        </div>
        <div className="client-appointments__side">
          <MoneyBadge item={item} />
          <div
            className={`client-appointments__plan-progress client-appointments__plan-progress--${planTone}`}
            title={`Запланировано ${cntPlan} из ${cnt}`}
          >
            <div className="client-appointments__plan-progress-text">
              {cntPlan}/{cnt}
            </div>
            <div className="client-appointments__plan-progress-track">
              <div
                className="client-appointments__plan-progress-fill"
                style={{ width: `${planPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Вкладка «Назначения» — карточки назначений (QSprClientUsl + ToolBar8).
 */
const ClientAppointments = ({ idClient, open }) => {
  const law = useSelector((state) => state.user.session?.law);
  const canPay = getLawFlag(law, 87) === 1;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  /** null — закрыто; "add" — добавление; item — редактирование */
  const [formTarget, setFormTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateApplying, setTemplateApplying] = useState(false);
  const [planDaysItem, setPlanDaysItem] = useState(null);
  const [autoPlanOpen, setAutoPlanOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [search, setSearch] = useState("");
  const requestIdRef = useRef(0);

  const loadItems = useCallback(() => {
    if (!idClient) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");

    fetchHealthClientUsl(idClient)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setRows(Array.isArray(data) ? data.map(normalizeUslRow) : []);
        setSelectedKeys([]);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить назначения");
        setRows([]);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [idClient]);

  useEffect(() => {
    if (!open || !idClient) return;
    setFormTarget(null);
    loadItems();
  }, [open, idClient, loadItems]);

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => String(row.tName || "").toLowerCase().includes(q));
  }, [rows, search]);

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((row) => selectedSet.has(row.key));

  const toggleSelectAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedKeys((prev) => prev.filter((key) => !visibleRows.some((row) => row.key === key)));
      return;
    }
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      visibleRows.forEach((row) => next.add(row.key));
      return [...next];
    });
  }, [allVisibleSelected, visibleRows]);

  const toggleKey = useCallback((key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const hasSelection = selectedKeys.length > 0;

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedSet.has(row.key)),
    [rows, selectedSet]
  );

  const selectedIdStrs = useMemo(
    () => selectedRows.map((row) => row.idStr).filter((id) => id != null),
    [selectedRows]
  );

  const selectedHasPlan = useMemo(
    () => selectedRows.some((row) => (toNumber(row.cntPlan) ?? 0) > 0),
    [selectedRows]
  );

  const canDeleteSelected = hasSelection && !selectedHasPlan && !deleting;

  const openAdd = useCallback(() => {
    setFormTarget("add");
  }, []);

  const openEdit = useCallback((item) => {
    setFormTarget(item);
  }, []);

  const closeForm = useCallback(() => {
    setFormTarget(null);
  }, []);

  const onFormSaved = useCallback(() => {
    setFormTarget(null);
    loadItems();
  }, [loadItems]);

  const onDeleteSelected = useCallback(() => {
    if (!idClient || !canDeleteSelected) return;

    const single = selectedRows.length === 1;
    const names = selectedRows
      .slice(0, 10)
      .map((row) => row.tName || `#${row.idStr}`);
    const extra = selectedRows.length - names.length;

    Modal.confirm({
      title: single ? "Удалить назначение?" : `Удалить назначения (${selectedRows.length})?`,
      content: (
        <div>
          {names.map((name, i) => (
            <div key={i}>{name}</div>
          ))}
          {extra > 0 ? <div>… и ещё {extra}</div> : null}
        </div>
      ),
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleting(true);
        try {
          for (const row of selectedRows) {
            await deleteHealthClientUsl(idClient, row.idStr);
          }
          if (
            formTarget &&
            formTarget !== "add" &&
            selectedRows.some((row) => row.key === formTarget.key)
          ) {
            setFormTarget(null);
          }
          message.success(single ? "Назначение удалено" : `Удалено назначений: ${selectedRows.length}`);
          loadItems();
        } catch (e) {
          message.error(e.message || "Не удалось удалить назначение");
          loadItems();
          throw e;
        } finally {
          setDeleting(false);
        }
      }
    });
  }, [idClient, canDeleteSelected, selectedRows, formTarget, loadItems]);

  const openTemplate = useCallback(() => {
    setFormTarget(null);
    setTemplateOpen(true);
  }, []);

  const openPlanDays = useCallback((item) => {
    setFormTarget(null);
    setPlanDaysItem(item);
  }, []);

  const closePlanDays = useCallback(() => {
    setPlanDaysItem(null);
  }, []);

  const closeTemplate = useCallback(() => {
    if (!templateApplying) setTemplateOpen(false);
  }, [templateApplying]);

  const onTemplateSelect = useCallback(
    (idShb, template) => {
      if (!idClient || idShb == null) return Promise.reject();
      setTemplateApplying(true);
      return applyHealthUslTemplate(idClient, idShb)
        .then(() => {
          setTemplateOpen(false);
          message.success(
            template?.name
              ? `Назначения из шаблона «${template.name}» добавлены`
              : "Назначения из шаблона добавлены"
          );
          loadItems();
        })
        .catch((e) => {
          message.error(e.message || "Не удалось добавить из шаблона");
          return Promise.reject(e);
        })
        .finally(() => setTemplateApplying(false));
    },
    [idClient, loadItems]
  );

  const formOpen = formTarget != null;
  const formItem = formTarget && formTarget !== "add" ? formTarget : null;

  const firstPlanableItem = useMemo(
    () => rows.find((row) => canPlanManual(row.vidPan)) || null,
    [rows]
  );

  const openPlanDaysFromToolbar = useCallback(() => {
    if (!firstPlanableItem) return;
    openPlanDays(firstPlanableItem);
  }, [firstPlanableItem, openPlanDays]);

  const openAutoPlan = useCallback(() => {
    if (!hasSelection) return;
    setAutoPlanOpen(true);
  }, [hasSelection]);

  const closeAutoPlan = useCallback(() => {
    setAutoPlanOpen(false);
  }, []);

  const onAutoPlanDone = useCallback(() => {
    setAutoPlanOpen(false);
    loadItems();
  }, [loadItems]);

  const hasUnpaid = useMemo(() => rows.some(hasDebt), [rows]);

  const openPay = useCallback(() => {
    if (!canPay || !hasUnpaid) return;
    setPayOpen(true);
  }, [canPay, hasUnpaid]);

  const closePay = useCallback(() => {
    setPayOpen(false);
  }, []);

  const onPayDone = useCallback(() => {
    setPayOpen(false);
    loadItems();
  }, [loadItems]);

  return (
    <div className="client-appointments client-tab">
      <div className="client-appointments__toolbar">
        <div className="client-appointments__toolbar-left">
          <button
            type="button"
            className="client-appointments__btn client-appointments__btn--primary"
            onClick={openAdd}
          >
            <ToolbarIcon src={plusSrc} />
            Добавить
          </button>
          <button
            type="button"
            className="client-appointments__btn"
            disabled={templateApplying}
            onClick={openTemplate}
          >
            <ToolbarIcon src={fileTextSrc} />
            Из шаблона
          </button>
          <button
            type="button"
            className="client-appointments__btn"
            disabled={!hasSelection}
            onClick={openAutoPlan}
          >
            <ToolbarIcon src={zapSrc} />
            Автоплан.
          </button>
          {canPay ? (
            <button
              type="button"
              className="client-appointments__btn"
              disabled={!hasUnpaid}
              onClick={openPay}
              title={hasUnpaid ? "Оплата назначений" : "Нет долга по платным назначениям"}
            >
              <ToolbarIcon src={walletSrc} />
              Оплата
            </button>
          ) : null}
          <button
            type="button"
            className="client-appointments__btn"
            disabled={!firstPlanableItem || hasSelection}
            onClick={openPlanDaysFromToolbar}
          >
            <ToolbarIcon src={calendarSrc} />
            План
          </button>
          <Tooltip title={allVisibleSelected ? "Снять выделение" : "Выделить все назначения"}>
            <span>
              <button
                type="button"
                className={`client-appointments__icon-btn${
                  allVisibleSelected ? " client-appointments__icon-btn--active" : ""
                }`}
                disabled={!visibleRows.length}
                aria-label={allVisibleSelected ? "Снять выделение" : "Выделить все назначения"}
                onClick={toggleSelectAll}
              >
                <ToolbarIcon src={listSrc} />
              </button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              !hasSelection
                ? "Выделите назначения для удаления"
                : selectedHasPlan
                  ? "Среди выделенных есть назначения с планированием — удаление недоступно"
                  : "Удалить выделенные назначения"
            }
          >
            <span>
              <button
                type="button"
                className="client-appointments__icon-btn client-appointments__icon-btn--danger"
                disabled={!canDeleteSelected}
                aria-label="Удалить выделенные назначения"
                onClick={onDeleteSelected}
              >
                <DeleteOutlined />
              </button>
            </span>
          </Tooltip>
        </div>
        <div className="client-appointments__toolbar-right">
          <div className="client-appointments__search">
            <ToolbarIcon src={searchSrc} />
            <Input
              variant="borderless"
              value={search}
              placeholder="Поиск"
              allowClear
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? (
        <Alert className="client-appointments__error" type="error" showIcon message={error} />
      ) : null}

      <div className="client-appointments__body">
        {loading && !rows.length ? (
          <div className="client-appointments__center">
            <Spin tip="Загрузка назначений..." />
          </div>
        ) : visibleRows.length ? (
          <ul className="client-appointments__list">
            {visibleRows.map((item) => (
              <li key={item.key}>
                <AppointmentCardView
                  item={item}
                  selected={selectedSet.has(item.key)}
                  onToggle={toggleKey}
                  onEdit={openEdit}
                  onPlanDays={openPlanDays}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className="client-appointments__center">Нет назначений</div>
        )}
      </div>

      <ClientAppointmentFormDrawer
        open={formOpen}
        idClient={idClient}
        item={formItem}
        onClose={closeForm}
        onSaved={onFormSaved}
      />

      <ClientAppointmentTemplateModal
        open={templateOpen}
        onCancel={closeTemplate}
        onSelect={onTemplateSelect}
      />

      <ClientAppointmentPlanDaysDrawer
        open={Boolean(planDaysItem)}
        idClient={idClient}
        idStr={planDaysItem?.idStr}
        appointments={rows}
        onClose={closePlanDays}
        onChanged={loadItems}
      />

      <ClientAppointmentAutoPlanDrawer
        open={autoPlanOpen}
        idClient={idClient}
        appointments={rows}
        selectedIdStrs={selectedIdStrs}
        onClose={closeAutoPlan}
        onDone={onAutoPlanDone}
      />

      <ClientAppointmentPayDrawer
        open={payOpen}
        idClient={idClient}
        onClose={closePay}
        onDone={onPayDone}
      />
    </div>
  );
};

export default ClientAppointments;
